import React, { FC, useRef, useState } from 'react';

import { Check, Close, PauseCircleOutline } from '@mui/icons-material';
import CommitOutlinedIcon from '@mui/icons-material/CommitOutlined';
import PendingIcon from '@mui/icons-material/Pending';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  LinearProgress,
  linearProgressClasses,
  Stack,
  styled,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import saveAs from 'file-saver';

import { riPrimary } from '../../RiverusUI/Theme/colors';
import { generateTags } from '../../Templates/Component/DocGenerate';

export async function retryWithMaxAttempts(
  action: any,
  maxRetries: number,
  delay = 2000,
  ...args: any
) {
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      return await action(...args);
    } catch (error) {
      attempt++;
      console.error(`Error on attempt ${attempt}:`, error);

      if (attempt === maxRetries) {
        console.error('Max retries reached. Throwing error...');
        throw error; // Re-throw the error if max retries are reached
      }

      // Optional: Delay before the next attempt
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay * 2));
      }
    }
  }
}

export const convertPDF = async (Core: any, fileURL: any) => {
  const buf = await Core.officeToPDFBuffer(fileURL);
  // Optionally save the blob to a file or upload to a server
  const blob = new Blob([buf], { type: 'application/pdf' });
  const file = blob;
  if (file) {
    return file;
  } else {
    throw new Error('Network issue');
  }
};

export const downloadFile = (blob: any, fileName: string, fileExt: string) => {
  const a = document.createElement('a');
  document.body.appendChild(a);
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = `${fileName}.${fileExt}`;
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 0);
};

export const removeLineBreaks = (str: string) => {
  str = str.replace(/\r?\n|\r/g, ' '); // Remove line breaks
  str = str.replace(/\b([IVXLCDMivxlcdm]+)\.\s+/g, '$1.');
  return str;
};

export const highlightedSentence = (
  sentence: string,
  instance: any,
  setOpenErrorMsg?: any
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (!instance) {
      reject('PDFTron instance not available');
      return;
    }

    const { Core } = instance;
    const { documentViewer, Annotations, annotationManager } = Core;
    const annotations = annotationManager.getAnnotationsList();
    const searchMode =
      instance.Core.Search.Mode.PAGE_STOP | instance.Core.Search.Mode.HIGHLIGHT;
    let textPresent = 0;
    const searchOptions = {
      fullSearch: true,
      onResult: (result: any) => {
        if (result.resultCode === instance.Core.Search.ResultCode.FOUND) {
          textPresent = 1;
          const highlight = new Annotations.TextHighlightAnnotation();
          result.quads.forEach((quad: any) => {
            highlight.Quads.push(quad.getPoints());
          });

          highlight.PageNumber = result.pageNum;
          annotationManager.addAnnotation(highlight);
          annotationManager.redrawAnnotation(highlight);
          annotationManager.drawAnnotations({ pageNumber: result.pageNum });

          if (documentViewer.getCurrentPage() !== result.pageNum) {
            documentViewer.setCurrentPage(result.pageNum, true);
          }
        }
      },
      onDocumentEnd: () => {
        if (textPresent === 0) {
          setOpenErrorMsg(true);
        }
        resolve(); // Resolve the promise once highlighting is completed
      },
    };
    documentViewer.textSearchInit(
      removeLineBreaks(sentence),
      searchMode,
      searchOptions
    );
    annotationManager.deleteAnnotations(annotations);
  });
};

export const getTodayDate = (type?: string) => {
  const timeElapsed = Date.now();
  const date = new Date(timeElapsed);

  const time = date.toLocaleString([], { hour: '2-digit', minute: '2-digit' });

  const fullTime = date.toLocaleString([], { hour12: true });

  const [month, day, year] = [
    date.getMonth(),
    date.getDate(),
    date.getFullYear(),
  ];

  switch (type) {
    case 'full-date-and-time':
      return `${day}/${month}/${year} ${fullTime}`;
    case 'full-date':
      return `${day}/${month}/${year}`;
    case 'full-time':
      return `${fullTime}`;
    case 'month':
      return month;
    case 'day':
      return day;
    case 'year':
      return year;
    default:
      return `${day}/${month}/${year} ${time}`;
  }
};

export const applyFields = async (instance: any) => {
  if (!instance) return;
  const { Annotations, documentViewer } = instance.Core;
  const annotationManager = documentViewer.getAnnotationManager();
  const fieldManager = annotationManager.getFieldManager();
  const annotationsList = annotationManager.getAnnotationsList();
  const doc = await documentViewer.getDocument().getPDFDoc();
  doc?.initSecurityHandler();
  doc?.lock();
  const annotsToDraw: any = [];

  await Promise.all(
    annotationsList.map(async (annot: any, index: number) => {
      let inputAnnot;
      let field;

      if (annot.getCustomData('type') === 'SIGNATURE') {
        const flags = new instance.Core.Annotations.WidgetFlags({});
        flags.set('Required', true);
        field = new Annotations.Forms.Field(
          `${annot.getContents()}-${Date.now()}-${index} ${annot.getCustomData(
            'value'
          )}`,
          {
            type: 'Sig',
            flags: flags,
          }
        );
        inputAnnot = new Annotations.SignatureWidgetAnnotation(field, {
          appearance: '_DEFAULT',
          appearances: {
            _DEFAULT: {
              Normal: {
                data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjEuMWMqnEsAAAANSURBVBhXY/j//z8DAAj8Av6IXwbgAAAAAElFTkSuQmCC',
                offset: {
                  x: 100,
                  y: 100,
                },
              },
            },
          },
          ...(annot.getCustomData('initials') === 'true'
            ? { requiresInitials: true }
            : {}),
        });

        // set position
        inputAnnot.PageNumber = annot.getPageNumber();
        inputAnnot.X = annot.getX();
        inputAnnot.Y = annot.getY();
        inputAnnot.rotation = annot.Rotation;
        if (annot.Rotation === 0 || annot.Rotation === 180) {
          inputAnnot.Width = annot.getWidth();
          inputAnnot.Height = annot.getHeight();
          annot.FontSize = `${annot.getHeight() / 2 + 3.5}px`;
        } else {
          inputAnnot.Width = annot.getHeight();
          inputAnnot.Height = annot.getWidth();
          annot.FontSize = `${annot.getWidth() / 2 + 3.5}px`;
        }
        inputAnnot.setCustomData('user', annot.getCustomData('value'));
        if (annot.getCustomData('initials') === 'true') {
          inputAnnot.setCustomData('trn-signature-type', 'initialsSignature');
          inputAnnot.setCustomData('type', 'initials');
        } else {
          inputAnnot.setCustomData('trn-signature-type', 'fullSignature');
          inputAnnot.setCustomData('type', 'signature');
        }
      } else {
        return;
      }
      const originalColor = annot.TextColor || annot.StrokeColor; // PDFTron Color objects have R, G, B properties (or r, g, b)
      const textColorR = originalColor?.R ?? originalColor?.r ?? 0;
      const textColorG = originalColor?.G ?? originalColor?.g ?? 0;
      const textColorB = originalColor?.B ?? originalColor?.b ?? 0;
      annot.TextColor = new Annotations.Color(
        textColorR,
        textColorG,
        textColorB
      );
      annot.FillColor = new Annotations.Color(255, 255, 255, 0);
      annot.StrokeThickness = 0;
      annot.TextAlign = 'left';
      if (annot.Rotation === 180) {
        annot.Y = annot.getY() - annot.getHeight() - 4;
      } else {
        annot.Y = annot.getY() + annot.getHeight() + 4;
      }
      annot.setCustomData('user', annot.getCustomData('value'));

      // draw the annotation the viewer
      annotationManager.addAnnotation(inputAnnot);
      fieldManager.addField(field);
      annotsToDraw.push(inputAnnot);
    })
  );
  // refresh viewer
  await annotationManager.drawAnnotationsFromList(annotsToDraw);
};

export const removeAnnotation = async (
  instance: any,
  tempSignatoryId?: any
) => {
  if (!instance) return;
  const { documentViewer, Annotations } = instance.Core;
  const annotationManager = documentViewer.getAnnotationManager();
  const annots = annotationManager.getAnnotationsList();
  const annotsToDraw: any = [];

  annots.forEach((annot: any) => {
    if (
      (annot instanceof Annotations.SignatureWidgetAnnotation &&
        annot.getCustomData('user') === tempSignatoryId) ||
      tempSignatoryId === annot.getCustomData('value')
    ) {
      annotationManager.deleteAnnotation(annot);
    }
  });

  // delete old annotations
  // refresh viewer
  await annotationManager.drawAnnotationsFromList(annotsToDraw);
};

export const handleSeeMoreAndLess = (text: string, length = 20) => {
  const words = text?.split(/\s+/);
  if (words?.length > length) {
    const sentence = words?.slice(0, length)?.join(' ');
    return `${sentence}...`;
  } else {
    return words?.join(' ');
  }
};

export const formatPayloadDate = (date: Date | null): string => {
  if (!date) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().split('T')[0];
};
export const download_file = (data: any, file_name?: string) => {
  const filename = data?.file_name;

  fetch(data?.location)
    .then((res) => res.blob())
    .then((blobData) => {
      saveAs(blobData, filename ? filename : file_name);
    });
};

export const sortListAlphabetical = (list: any, labelKey?: string) => {
  if (Array.isArray(list)) {
    const sortAlphabetical = [...(list || [])]?.sort((a: any, b: any) => {
      return a?.[labelKey || 'name']?.localeCompare(b?.[labelKey || 'name']);
    });
    return sortAlphabetical || [];
  }
  return [];
};

export const droppedMsg =
  'This action can’t be performed, the draft has been dropped by the owner!';

export const automaticallyDroppedMsg =
  'This action can’t be performed, the connected document has been terminated!';

export const signatureMethod = [
  'imageSignaturePanelButton',
  'inkSignaturePanelButton',
  'textSignaturePanelButton',
];

export function getTimezoneAbbreviation(date: any) {
  // Convert the date into human readable
  // An example: Fri Jul 09 2021 13:07:25 GMT+0200 (Central European Summer Time)
  // As you can see there is the timezone

  date = date.toString();

  // We get the timezone matching everything the is inside the brackets/parentheses
  let timezone = date.match(/\(.+\)/g)[0];

  // We remove the brackets/parentheses
  timezone = timezone.replace('(', '').replace(')', '');

  // A new variable for the abbreviation
  let abbreviation = '';

  // We run a forEach dividing the timezone in words with split
  timezone.split(' ').forEach((word: string) => {
    // We insert the first letter of every word in the abbreviation variable
    abbreviation += word.split('')[0];
  });

  // And finally we return the abbreviation
  return abbreviation;
}

export const draftTabStatus = [
  'internal draft created',
  'deviation approval pending',
  'pre signatories approval pending',
  'counter party draft uploaded',
  'legal review requested',
  'legal review complete',
  'pending with counterparty',
  'pre signatories approved',
];

export const isStatusMatched = (status: any) => {
  return draftTabStatus?.some((item) => status?.toLowerCase().includes(item));
};

export const counterPartyOptions = [
  'Counter Party Draft Uploaded',
  'Legal Review Requested',
  'Legal Review Complete',
];

export const editableStatusMap: Record<string, string[]> = {
  'Internal Draft Created': [
    'Legal Review Requested',
    'Legal Review Complete',
    'Pending With Counterparty',
  ],
  'Legal Review Requested': [
    'Internal Draft Created',
    'Legal Review Complete',
    'Pending With Counterparty',
  ],
  'Legal Review Complete': [
    'Internal Draft Created',
    'Legal Review Requested',
    'Pending With Counterparty',
  ],
  'Pending With Counterparty': [
    'Internal Draft Created',
    'Legal Review Requested',
    'Legal Review Complete',
  ],
};

// Prevent duplicate comments submission
export const handleEnterKeySubmit = (
  event: React.KeyboardEvent<HTMLInputElement>,
  id: string,
  isSubmittingRef: React.MutableRefObject<{ [key: string]: boolean }>,
  onSubmit: (data: any, id: string) => void
) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();

    if (isSubmittingRef.current[id]) return;
    isSubmittingRef.current[id] = true;

    const inputElement = event.target as HTMLInputElement;
    const commentText = inputElement?.value?.trim();

    if (commentText) {
      const commentData = { [`comment-${id}`]: commentText };
      onSubmit(commentData, id);
      inputElement.value = ''; // Clears input field after submission
    }
    // Enable input field after delay
    setTimeout(() => {
      isSubmittingRef.current[id] = false;
    }, 500);
  }
};

export const countryList = [
  { name: 'Afghanistan' },
  { name: 'Albania' },
  { name: 'Algeria' },
  { name: 'Andorra' },
  { name: 'Angola' },
  { name: 'Antigua and Barbuda' },
  { name: 'Argentina' },
  { name: 'Armenia' },
  { name: 'Australia' },
  { name: 'Austria' },
  { name: 'Azerbaijan' },
  { name: 'Bahamas' },
  { name: 'Bahrain' },
  { name: 'Bangladesh' },
  { name: 'Barbados' },
  { name: 'Belarus' },
  { name: 'Belgium' },
  { name: 'Belize' },
  { name: 'Benin' },
  { name: 'Bhutan' },
  { name: 'Bolivia' },
  { name: 'Bosnia and Herzegovina' },
  { name: 'Botswana' },
  { name: 'Brazil' },
  { name: 'Brunei' },
  { name: 'Bulgaria' },
  { name: 'Burkina Faso' },
  { name: 'Burundi' },
  { name: 'Cabo Verde' },
  { name: 'Cambodia' },
  { name: 'Cameroon' },
  { name: 'Canada' },
  { name: 'Chad' },
  { name: 'Chile' },
  { name: 'China' },
  { name: 'Colombia' },
  { name: 'Comoros' },
  { name: 'Costa Rica' },
  { name: 'Croatia' },
  { name: 'Cuba' },
  { name: 'Cyprus' },
  { name: 'Czech Republic' },
  { name: 'Czechia' },
  { name: 'Democratic Republic of the Congo' },
  { name: 'Denmark' },
  { name: 'Djibouti' },
  { name: 'Dominica' },
  { name: 'Dominican Republic' },
  { name: 'Ecuador' },
  { name: 'Egypt' },
  { name: 'El Salvador' },
  { name: 'Equatorial Guinea' },
  { name: 'Eritrea' },
  { name: 'Estonia' },
  { name: 'Eswatini' },
  { name: 'Ethiopia' },
  { name: 'Fiji' },
  { name: 'Finland' },
  { name: 'France' },
  { name: 'Gabon' },
  { name: 'Gambia' },
  { name: 'Georgia' },
  { name: 'Germany' },
  { name: 'Ghana' },
  { name: 'Greece' },
  { name: 'Grenada' },
  { name: 'Guatemala' },
  { name: 'Guinea' },
  { name: 'Guinea-Bissau' },
  { name: 'Guyana' },
  { name: 'Haiti' },
  { name: 'Holy See' },
  { name: 'Honduras' },
  { name: 'Hungary' },
  { name: 'Iceland' },
  { name: 'India' },
  { name: 'Indonesia' },
  { name: 'Iran' },
  { name: 'Iraq' },
  { name: 'Ireland' },
  { name: 'Israel' },
  { name: 'Italy' },
  { name: 'Jamaica' },
  { name: 'Japan' },
  { name: 'Jordan' },
  { name: 'Kazakhstan' },
  { name: 'Kenya' },
  { name: 'Kiribati' },
  { name: 'Korea' },
  { name: 'Kuwait' },
  { name: 'Kyrgyzstan' },
  { name: 'Laos' },
  { name: 'Latvia' },
  { name: 'Lebanon' },
  { name: 'Lesotho' },
  { name: 'Liberia' },
  { name: 'Libya' },
  { name: 'Liechtenstein' },
  { name: 'Lithuania' },
  { name: 'Luxembourg' },
  { name: 'Madagascar' },
  { name: 'Malawi' },
  { name: 'Malaysia' },
  { name: 'Maldives' },
  { name: 'Mali' },
  { name: 'Malta' },
  { name: 'Marshall Islands' },
  { name: 'Mauritania' },
  { name: 'Mauritius' },
  { name: 'Mexico' },
  { name: 'Micronesia' },
  { name: 'Moldova' },
  { name: 'Monaco' },
  { name: 'Mongolia' },
  { name: 'Montenegro' },
  { name: 'Morocco' },
  { name: 'Mozambique' },
  { name: 'Myanmar' },
  { name: 'Namibia' },
  { name: 'Nauru' },
  { name: 'Nepal' },
  { name: 'Netherlands' },
  { name: 'New Zealand' },
  { name: 'Nicaragua' },
  { name: 'Niger' },
  { name: 'Nigeria' },
  { name: 'North Macedonia' },
  { name: 'Norway' },
  { name: 'Oman' },
  { name: 'Pakistan' },
  { name: 'Palau' },
  { name: 'Palestine' },
  { name: 'Panama' },
  { name: 'Papua New Guinea' },
  { name: 'Paraguay' },
  { name: 'Peru' },
  { name: 'Philippines' },
  { name: 'Poland' },
  { name: 'Portugal' },
  { name: 'Qatar' },
  { name: 'Republic of Belarus' },
  { name: 'Romania' },
  { name: 'Russia' },
  { name: 'Rwanda' },
  { name: 'Saint Kitts and Nevis' },
  { name: 'Saint Lucia' },
  { name: 'Saint Vincent and the Grenadines' },
  { name: 'Samoa' },
  { name: 'San Marino' },
  { name: 'Sao Tome and Principe' },
  { name: 'Saudi Arabia' },
  { name: 'Senegal' },
  { name: 'Serbia' },
  { name: 'Seychelles' },
  { name: 'Sierra Leone' },
  { name: 'Singapore' },
  { name: 'Slovakia' },
  { name: 'Slovenia' },
  { name: 'Solomon Islands' },
  { name: 'Somalia' },
  { name: 'South Africa' },
  { name: 'South Sudan' },
  { name: 'Spain' },
  { name: 'Sri Lanka' },
  { name: 'Sudan' },
  { name: 'Suriname' },
  { name: 'Sweden' },
  { name: 'Switzerland' },
  { name: 'Syria' },
  { name: 'Taiwan' },
  { name: 'Tajikistan' },
  { name: 'Tanzania' },
  { name: 'Thailand' },
  { name: 'Timor-Leste' },
  { name: 'Togo' },
  { name: 'Tonga' },
  { name: 'Trinidad and Tobago' },
  { name: 'Tunisia' },
  { name: 'Turkey' },
  { name: 'Turkmenistan' },
  { name: 'Tuvalu' },
  { name: 'Uganda' },
  { name: 'Ukraine' },
  { name: 'United Arab Emirates' },
  { name: 'United Kingdom' },
  { name: 'United States of America' },
  { name: 'Uruguay' },
  { name: 'Uzbekistan' },
  { name: 'Vanuatu' },
  { name: 'Vatican City' },
  { name: 'Venezuela' },
  { name: 'Vietnam' },
  { name: 'Yemen' },
  { name: 'Zambia' },
  { name: 'Zimbabwe' },
];

export const innerTabStyle = {
  backgroundColor: riPrimary[10],
  borderRadius: '20px',
  paddingX: '15px',
};

export const disabledButton = {
  color: 'rgba(0, 0, 0, 0.26)',
  boxShadow: 'none',
  background: 'rgba(0, 0, 0, 0.12)',
  '&:hover': {
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
    background: 'rgba(0, 0, 0, 0.12)',
  },
};

export const EditDocumentSaveMenu: any[] = [
  {
    value: 'save_draft',
    label: 'Save Draft',
    startIcon: <SaveOutlinedIcon />,
  },
  {
    value: 'save_and_commit',
    label: 'Save and commit',
    startIcon: <CommitOutlinedIcon />,
  },
];

export const parseTagList = (tags: any) => {
  const parsedTags = Object.keys(tags).reduce((acc, curr) => {
    let innerObject = {};
    if (Object.keys(tags[curr]).length > 0) {
      innerObject = { ...innerObject, ...tags[curr] };
    }
    return { ...acc, [curr.split(' ')[0]]: {}, ...innerObject };
  }, {});
  return parsedTags;
};

export const getTags = (url: string) => {
  return new Promise<any>((resolve) => {
    generateTags(url, (tags: any) => {
      const parsed = parseTagList(tags);
      resolve(parsed);
    });
  });
};

export const getFieldValue = (type: string, value: any, field?: any) => {
  if (type === 'boolean') {
    if (field?.set_recommended_value?.default_field_value) {
      return typeof value === 'string' ? value : String(value ?? '');
    } else {
      return String(value).toLowerCase() === 'true';
    }
  }
  if (type === 'bulleted list' || type === 'numbered list') {
    if (value && value !== '') {
      return JSON.parse(value);
    }
    return [{ list: '' }];
  }
  if (type === 'assignee') {
    if (value && value !== '') {
      return JSON.parse(value);
    }
    return [];
  }
  if (type === 'percentage') {
    return value?.toString().replace('%', '').trim();
  }
  if (type === 'currency') {
    return value
      ?.toString()
      .replace(/^.+\)\s*/, '')
      .trim();
  }
  if (type === 'table') {
    if (value && value !== '') {
      return JSON.parse(value);
    }
    return [];
  }
  return value || '';
};

export const getFieldName = (
  mergeTag: string,
  fieldName: string,
  optionalFieldsLength: number
) => {
  const name = fieldName?.trim() || '';
  if (optionalFieldsLength) {
    return name;
  } else {
    try {
      const tagName = mergeTag.replace(/[{}]/g, '');
      // match suffix that is either a number or number + 1 letter only
      const suffixMatch = tagName.match(/\d+[A-Za-z]?$/);
      const suffix = suffixMatch?.[0] || '';
      if (suffix && !name.endsWith(suffix)) {
        return `${name} ${suffix}`;
      }
      return name;
    } catch (error) {
      console.error(error);
    }
  }
};
export const mapRecommendedValues = (
  items: { value1: string; value2: string }[] = []
) =>
  items?.flatMap(({ value1, value2 }) => [
    { label: value1, value: value1 },
    { label: value2, value: value2 },
  ]);

export const sortCounterPartyFirst = (parties: Array<any>) => {
  return parties
    ? JSON.parse(JSON.stringify(parties)).sort((a: any, b: any) => {
        const typeA = a.party_type?.toLowerCase();
        const typeB = b.party_type?.toLowerCase();

        if (typeA === 'counter_party' && typeB !== 'counter_party') return -1;
        if (typeA !== 'counter_party' && typeB === 'counter_party') return 1;
        return 0;
      })
    : [];
};

export const getMimeType = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    zip: 'application/zip',
    rar: 'application/vnd.rar',
    json: 'application/json',
  };

  return mimeTypes[extension || ''] || 'application/octet-stream';
};

export const getLatestMajorVersionId = (allDraftsId: any, version: number) => {
  const majorVersion = allDraftsId
    ?.filter((item: any) => item.version_type === 'major')
    ?.find((item: any) =>
      Object.entries(item).find(([, val]) => val === version)
    );

  let majorVersionId = '';

  Object.entries(majorVersion).forEach(([key]) => {
    if (key !== 'version_type') {
      majorVersionId = key;
    }
  });

  return majorVersionId;
};

export function getOrdinalSuffix(num: number) {
  const j = num % 10,
    k = num % 100;
  if (j === 1 && k !== 11) return num + 'st';
  if (j === 2 && k !== 12) return num + 'nd';
  if (j === 3 && k !== 13) return num + 'rd';
  return num + 'th';
}

export function numberToOrdinalWord(num: number) {
  const ordinals = [
    'Zeroth',
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth',
    'Thirteenth',
    'Fourteenth',
    'Fifteenth',
    'Sixteenth',
    'Seventeenth',
    'Eighteenth',
    'Nineteenth',
    'Twentieth',
  ];

  if (num >= 0 && num <= 20) {
    return ordinals[num];
  }

  // For 21 and above: fallback to num + ordinal suffix (e.g., "21st", "22nd")
  return getOrdinalSuffix(num);
}

export function joinWithAnd(arr: any) {
  if (arr.length === 0) {
    return '';
  } else if (arr.length === 1) {
    return String(arr[0]);
  } else {
    const allButLast = arr.slice(0, arr.length - 1);
    const lastElement = arr[arr.length - 1];
    return allButLast.join(', ') + ' and ' + lastElement;
  }
}

export const getTimeStringFromHours = (hours: string) => {
  const time = hours.split('.');
  if (time.length > 1) {
    const hrs = time[0];
    const mins = Math.round(parseFloat('0.' + time[1]) * 60);
    if (hrs === '0') {
      return `${mins} ${mins === 1 ? 'Minute' : 'Minutes'}`;
    }
    return `${hrs} ${hrs === '1' || hrs === '0' ? 'Hour' : 'Hours'} ${mins} ${
      mins === 1 || mins === 0 ? 'Minute' : 'Minutes'
    }`;
  }
  return `${hours} Hours`;
};

export const getStatusString = (status: string) => {
  if (status === 'In Progress')
    return (
      <Stack
        direction="row"
        alignItems="center"
        color="#0b62b3"
        fontSize="14px"
        gap="4px"
      >
        In Progress <PendingIcon sx={{ fontSize: '14px' }} />
      </Stack>
    );
  if (status === 'Breached')
    return (
      <Stack
        direction="row"
        alignItems="center"
        component="span"
        color="red"
        fontSize="14px"
        gap="4px"
      >
        Breached <Close sx={{ fontSize: '14px' }} />
      </Stack>
    );
  if (status === 'Completed')
    return (
      <Stack
        direction="row"
        alignItems="center"
        color="green"
        fontSize="14px"
        gap="4px"
      >
        Completed <Check sx={{ fontSize: '14px' }} />
      </Stack>
    );
  if (status === 'Fulfilled')
    return (
      <Stack
        direction="row"
        alignItems="center"
        color="green"
        fontSize="14px"
        gap="4px"
      >
        Fulfilled <Check sx={{ fontSize: '14px' }} />
      </Stack>
    );
  if (status === 'Paused')
    return (
      <Stack
        direction="row"
        alignItems="center"
        color="orange"
        fontSize="14px"
        gap="4px"
      >
        Paused <PauseCircleOutline sx={{ fontSize: '14px' }} />
      </Stack>
    );
  if (status === 'started') return 'Started';
  return status;
};

export const formatDate = (newDate?: any): string | undefined => {
  return newDate?.replace(/\.\d{6}/, '')?.replace(/\+00:00$/, 'Z');
};

export const timezoneRegex = /\s([A-Za-z]{3,4})$/;
export const timeRegex = /(?:Z|[+-]\d{2}:\d{2})$/;

export const getValidatedDate = (cleanedDate?: string | null) =>
  cleanedDate
    ? dayjs(cleanedDate, [
        'DD/MM/YYYY, h:mm A [ZZZ]',
        'YYYY-MM-DD',
        'DD-MM-YYYY',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ssZ',
      ])
    : null;

export const taskNameMapping: Record<string, string> = {
  requisition_approvers: 'Requisition Approval',
  deviation_approvers: 'Policy Approval',
  pre_signatory_approvers: 'Pre Signatory Approval',
  kdp: 'Policy Approval',
  custom: 'Custom Approval',
  merge_tag: 'Merge Tag Approval',
  ai: 'AI Approval',
  Checklist: 'Checklist Fill-Up',
  signatory: 'Sign Document',
  OptionalFields: 'Template Field Fill-Up',
};

export const formatLabel = (value?: string) => {
  if (!value) return '';
  return value?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export function getActiveIds<T extends Record<string, any>>(
  list: T[] | undefined | null,
  {
    idKey = 'id',
    activeKey = 'active_status',
  }: { idKey?: keyof T; activeKey?: keyof T } = {}
): T[keyof T][] {
  if (!Array.isArray(list)) return [];
  return list.filter((item) => item?.[activeKey]).map((item) => item?.[idKey]);
}

export const getFileType = (url: string) => {
  if (url.includes('draft_notes')) {
    return 'draft_notes';
  } else if (url.includes('policy_notes')) {
    return 'policy_notes';
  } else if (url.includes('note_for_approval')) {
    return 'note_for_approval';
  } else if (url.includes('earlier_draft')) {
    return 'earlier_draft';
  } else if (url.includes('own_reference_document')) {
    return 'own_reference_document';
  }
};

export const RecurringOptions = [
  {
    value: 'last_day_of',
    title: 'Last day of a',
  },
  {
    value: 'day_of',
    title: 'Fixed day',
  },
  {
    value: 'recreate_every',
    title: 'Recreate every',
  },
];

export const sendTypeOption = [
  {
    value: 'now',
    title: 'Send Now',
  },
  {
    value: 'schedule',
    title: 'Schedule Later',
  },
];

export const weekdaysOptions = [
  {
    value: 0,
    label: 'M',
  },
  {
    value: 1,
    label: 'T',
  },
  {
    value: 2,
    label: 'W',
  },
  {
    value: 3,
    label: 'T',
  },
  {
    value: 4,
    label: 'F',
  },
  {
    value: 5,
    label: 'S',
  },
  {
    value: 6,
    label: 'S',
  },
];

export const tabsToColor = {
  my_drafts: '#e6e2f0',
  my_drafts_under_collaboration: '#00acc114',
  my_approval_pending: '#ffefef',
  my_standstill_draft: '#dcdcdc',
  my_signed_draft: '#deebff',
  my_signature_pending: '#f7eebf70',
  total: '#e6e2f0',
  drafts_under_collaboration: '#00acc114',
  approval_pending: '#ffefef',
  standstill_draft: '#dcdcdc',
  signed_draft: '#deebff',
  signature_pending: '#f7eebf70',
} as const;

export const getBadgeColor = (activeHeader: string): string => {
  return tabsToColor[activeHeader as keyof typeof tabsToColor] || '';
};

export const preSignatoryEmailOptions = [
  {
    value: 'AI',
    title: 'Ultra AI Summary',
  },
  {
    value: 'Linked Template',
    title: 'Linked Template Summary',
  },
  {
    value: 'Summary Document',
    title: 'Upload Summary Document',
  },
];

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === 'light' ? '#6D264C' : '#308fe8',
  },
}));

export const statusSorted = [
  { name: 'Block', value: 'CONFIRM' },
  { name: 'Suspect', value: 'SUSPECT' },
  { name: 'Clear', value: 'CLEAR' },
];

export const getStatusLabel = (status?: string) => {
  switch (status) {
    case 'CONFIRM':
      return 'BLOCK';
    case 'SUSPECT':
      return 'SUSPECT';
    case 'CLEAR':
      return 'CLEAR';
    default:
      return status ?? '';
  }
};

export const getSwitchColor = (status?: string) => {
  switch (status) {
    case 'CONFIRM':
      return '#d32f2f';
    case 'PENDING':
      return '#ed6c02';
    case 'SUSPECT':
      return '#c69316';
    case 'CLEAR':
      return '#008e07';
    default:
      return '#9e9e9e';
  }
};

export const generateColorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id?.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const r = (hash >> 16) & 255;
  const g = (hash >> 8) & 255;
  const b = hash & 255;
  return {
    r: Math.abs(r),
    g: Math.abs(g),
    b: Math.abs(b),
  };
};

export const canEditRecipients = (report: any) => {
  const today = dayjs().startOf('day');
  // Recurring report
  if (report?.frequency) {
    if (
      report?.next_triggered_date &&
      dayjs(report.next_triggered_date).isAfter(today)
    ) {
      return true;
    }
    return true;
  }
  // One-time report
  if (report?.scheduled_date) {
    return dayjs(report.scheduled_date).isAfter(today);
  }
  return false;
};

export const getActiveTab = (
  tabs: { value: string }[],
  tabFromLocal: string | null,
  defaultTab: string
) =>
  tabFromLocal && tabs?.some((tab) => tab?.value === tabFromLocal)
    ? tabFromLocal
    : defaultTab;

export const TruncateTooltip: FC<{
  tooltipTitle: string;
  children: React.ReactNode;
}> = ({ tooltipTitle, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const checkTruncation = () => {
    const inputEl = wrapperRef.current?.querySelector('input');
    if (inputEl) {
      setIsTruncated(inputEl.scrollWidth > inputEl.clientWidth);
    }
  };
  return (
    <Tooltip title={isTruncated ? tooltipTitle : ''} placement="top">
      <div
        ref={wrapperRef}
        onMouseEnter={checkTruncation}
        style={{ width: '100%' }}
      >
        {children}
      </div>
    </Tooltip>
  );
};
